import React from 'react';
import { Card } from '@/components/ui/card';
import { Layers, HardHat, Mountain, Wrench } from 'lucide-react';

const Products = () => {
    const products = [
        {
            icon: Layers,
            title: 'Bricks',
            description: 'Our bricks are manufactured using top-grade raw materials and advanced production techniques. They are designed to withstand the toughest conditions, ensuring the structural integrity of your buildings.',
            image: 'https://customer-assets.emergentagent.com/job_673bff6c-2308-4c32-b063-9be9a3a42c9a/artifacts/3f9za05n_WhatsApp%20Image%202024-01-05%20at%203.08.29%20PM.jpeg'
        },
        {
            icon: HardHat,
            title: 'Cement',
            description: 'We provide a variety of cement types tailored to different construction needs. Our cement products are known for their superior bonding properties and long-lasting performance.',
            image: 'https://customer-assets.emergentagent.com/job_673bff6c-2308-4c32-b063-9be9a3a42c9a/artifacts/9qdnnei5_WhatsApp%20Image%202024-01-05%20at%203.08.27%20PM.jpeg'
        },
        {
            icon: Mountain,
            title: 'Stone',
            description: 'Our selection of stones includes various types suitable for different purposes, such as decorative elements, paving, and structural components. Each stone is carefully sourced and processed.',
            image: 'https://customer-assets.emergentagent.com/job_673bff6c-2308-4c32-b063-9be9a3a42c9a/artifacts/o6xvrsxv_WhatsApp%20Image%202024-01-05%20at%203.08.28%20PM%20%281%29.jpeg'
        },
        {
            icon: Wrench,
            title: 'Steel',
            description: 'We offer a range of high-quality steel products, including bars, rods, and sheets, essential for reinforcing and building robust structures. Our steel products meet stringent industry standards.',
            image: 'https://customer-assets.emergentagent.com/job_673bff6c-2308-4c32-b063-9be9a3a42c9a/artifacts/0r1od4pu_WhatsApp%20Image%202024-01-05%20at%203.08.28%20PM.jpeg'
        }
    ];

    return (
        <section id="products" className="py-20 lg:py-28 bg-background">
            <div className="section-container">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <p className="text-primary font-medium text-sm uppercase tracking-wider mb-3">
                        Dhakad Enterprises - Build Your Dream Home
                    </p>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground section-title">
                        Our Products
                    </h2>
                    <p className="text-muted-foreground mt-6 max-w-3xl mx-auto">
                        At Dhakad Enterprises, we take pride in offering an extensive range of high-quality construction 
                        materials to meet all your building needs. Our commitment to excellence ensures that every product 
                        we supply is crafted to the highest standards.
                    </p>
                </div>

                {/* Products Grid */}
                <div className="grid md:grid-cols-2 gap-8">
                    {products.map((product, index) => (
                        <Card 
                            key={index}
                            className="group overflow-hidden bg-card border-border hover:border-primary/30 transition-all duration-300 hover:shadow-xl"
                        >
                            <div className="grid sm:grid-cols-5 h-full">
                                {/* Image */}
                                <div className="sm:col-span-2 h-48 sm:h-full overflow-hidden">
                                    <img 
                                        src={product.image}
                                        alt={product.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                </div>
                                
                                {/* Content */}
                                <div className="sm:col-span-3 p-6 flex flex-col">
                                    <div className="icon-box mb-4 w-12 h-12 group-hover:scale-110 transition-transform duration-300">
                                        <product.icon className="w-6 h-6 text-primary" />
                                    </div>
                                    <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                                        {product.title}
                                    </h3>
                                    <p className="text-muted-foreground text-sm leading-relaxed flex-grow">
                                        {product.description}
                                    </p>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>

                {/* Why Choose Section */}
                <div className="mt-20">
                    <div className="text-center mb-12">
                        <p className="text-primary font-medium text-sm uppercase tracking-wider mb-3">
                            Dhakad Enterprises
                        </p>
                        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
                            Why Choose Our Products?
                        </h2>
                        <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                            Choose Dhakad Enterprises for your construction material needs and experience 
                            the difference in quality, reliability, and service.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            {
                                number: '01',
                                title: 'Exceptional Quality',
                                description: 'Our materials are selected and processed with meticulous attention to detail, ensuring they meet the highest quality standards.'
                            },
                            {
                                number: '02',
                                title: 'Wide Range',
                                description: 'From foundational materials to finishing touches, we offer a comprehensive selection to cater to all aspects of construction.'
                            },
                            {
                                number: '03',
                                title: 'Reliability',
                                description: 'We understand the importance of reliable materials in construction projects. Our products are designed to deliver consistent results.'
                            },
                            {
                                number: '04',
                                title: 'Expertise',
                                description: 'With years of experience in the industry, we have the knowledge and expertise to provide products that meet evolving needs.'
                            }
                        ].map((item, index) => (
                            <Card 
                                key={index}
                                className="p-6 bg-card border-border hover:border-primary/30 transition-all duration-300 hover:shadow-lg group"
                            >
                                <span className="text-5xl font-bold text-primary/20 group-hover:text-primary/30 transition-colors">
                                    {item.number}
                                </span>
                                <h3 className="font-display text-lg font-semibold text-foreground mt-4 mb-2">
                                    {item.title}
                                </h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    {item.description}
                                </p>
                            </Card>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Products;
